import { sendAudioCommandm } from '@store/actions'
import { AppDispatch } from '@store/index'
import {
  IMediaRecorder,
  MediaRecorder,
  register,
} from 'extendable-media-recorder'
import { connect } from 'extendable-media-recorder-wav-encoder'
import { checkForVoiceActivity } from './sound.util'
import { ToothFeature, Tooth } from '@dataTypes/audio.types'
import { logger } from '@utils/safeLogger'

export const setuponDataAvailablem = async (
  recorder: React.MutableRefObject<IMediaRecorder | null>,
  paused: React.MutableRefObject<boolean>, // Correct use of useRef for a boolean
  mode: string[],
  audiofiles: React.MutableRefObject<Blob[]>,
  dispatch: AppDispatch,
  intervalms: React.MutableRefObject<number>,
  streamfilter: React.MutableRefObject<number>,
  setToothNumber: React.Dispatch<React.SetStateAction<number>>,
  setPosition: React.Dispatch<React.SetStateAction<string>>,
  setToothFeature: React.Dispatch<React.SetStateAction<ToothFeature>>,
  choiceCounter: React.MutableRefObject<number>,
  position: React.MutableRefObject<string>,
  toothNumber: React.MutableRefObject<number>,
  setClear: React.Dispatch<React.SetStateAction<boolean>>,
  prev_content: React.MutableRefObject<string>,
  session_id: React.MutableRefObject<string>,
  type: React.MutableRefObject<string>,
  currenttooth: React.MutableRefObject<Tooth>,
  wasVoiceDetectedRecently: React.MutableRefObject<boolean>,
  silenceStartTime: React.MutableRefObject<number | null>,
  setUndo: React.Dispatch<React.SetStateAction<boolean>>,
): Promise<void> => {
  /*
    Used to send voice audio segment as stream to voice assistant api when silence duration equals 800 ms
    Args:   
      recorder                (React.MutableRefObject<IMediaRecorder | null>):  IMediaRecorder react reference object use to record wave audio segments
      stream                     (React.MutableRefObject<MediaStream | null>):  MediaStream Object to stream audio from microphone 
      isRecording                           (React.MutableRefObject<boolean>):  Boolean to indicate if recorder is recording
      paused                                (React.MutableRefObject<boolean>):  Boolean to tell server to ignore transcripts
      audiofiles                             (React.MutableRefObject<Blob[]>):  Blob of audiofile in wav form
      dispatch:                                                 (AppDispatch):  Dispatch to invoke store action 
      intervalms:                            (React.MutableRefObject<number>):  (Depracated) Interval for SetInterval function
      streamfilter:                          (React.MutableRefObject<number>):  (Depracated) Volume/Amplitude filter
      setToothNumber:          (React.Dispatch<React.SetStateAction<number>>):  Dispatch function to change React state toothnumber
      setPosition:             (React.Dispatch<React.SetStateAction<string>>):  Dispatch function to change React state position
      setToothFeature:   (React.Dispatch<React.SetStateAction<ToothFeature>>):  Dispatch function to change React state toothfeature 
      choiceCounter:                         (React.MutableRefObject<number>):  (Depracated) used to count errors
      position:                              (React.MutableRefObject<string>):  A react ref object to reflect changes in position realtime
      toothNumber:                           (React.MutableRefObject<number>):  A react ref object to reflect changes in toothnumber realtime
      setClear:               (React.Dispatch<React.SetStateAction<boolean>>):  Dispatch function to change React state clear
      prev_content:                          (React.MutableRefObject<string>):  (Depracated) used to store previous data before new changes
      session_id:                            (React.MutableRefObject<string>):  Unique Identifier per user per session of voice assistant api
      type:                                  (React.MutableRefObject<string>):  String Identifier to invoke either (free/keyword) mode
      currenttooth:                           (React.MutableRefObject<Tooth>):  A ref object containing tooth number, position, type, pause
      wasVoiceDetectedRecently:             (React.MutableRefObject<boolean>):  Boolean to check for voce activity on streamed audio
      silenceStartTime:               (React.MutableRefObject<number | null>):  Inital time used to measure silence duration
      setUndo:                (React.Dispatch<React.SetStateAction<boolean>>):  Dispatch function used to change react state undo and to invoke undo command
  */
  if (!recorder.current) {
    throw new Error('Recorder must not be none')
  }

  recorder.current.ondataavailable = async (e: BlobEvent) => {
    const isVoice = await checkForVoiceActivity(e.data)

    const now = Date.now()

    // ✅ Always push the first chunk, even if silent (likely includes WAV header)
    if (audiofiles.current.length === 0) {
      audiofiles.current.push(e.data)
    }

    if (isVoice) {
      wasVoiceDetectedRecently.current = true
      silenceStartTime.current = null
      if (audiofiles.current[audiofiles.current.length - 1] !== e.data) {
        audiofiles.current.push(e.data)
      }
      return
    }
    // If no voice and no prior voice, ignore this silence chunk
    if (!wasVoiceDetectedRecently.current) {
      return
    }

    // If this is the first silent chunk, start silence timer
    if (!silenceStartTime.current) {
      silenceStartTime.current = now
      return
    }

    const silenceDuration = now - silenceStartTime.current

    if (silenceDuration < 600) {
      return // keep waiting
    }

    // Silence lasted long enough → finalize and send
    wasVoiceDetectedRecently.current = false
    silenceStartTime.current = null

    if (audiofiles.current.length === 0) return

    const view = new DataView(await audiofiles.current[0].arrayBuffer())
    const waveBlob = new Blob(audiofiles.current, { type: 'audio/wav' })
    audiofiles.current = []

    const audiocommand = 'audiodatacommand' + Date.now()
    try {
      await dispatch(
        sendAudioCommandm({
          data: waveBlob,
          isFirst: true,
          filename: audiocommand,
          stream: true,
          mode,
          setToothNumber,
          setPosition,
          setToothFeature,
          intervalms,
          streamfilter,
          choiceCounter,
          position,
          toothNumber,
          setClear,
          prev_content,
          session_id,
          type,
          currenttooth,
          setUndo,
        }),
      ).unwrap()
    } catch (err) {
      logger.error('Error sending audio:', err)
    }

    // Always clear buffer at the end
    audiofiles.current = []
  }
}

export const startRecordingm = async (
  recorder: React.MutableRefObject<IMediaRecorder | null>,
  mode: string[],
  stream: React.MutableRefObject<MediaStream | null>,
  isRecording: React.MutableRefObject<boolean>,
  paused: React.MutableRefObject<boolean>,
  audiofiles: React.MutableRefObject<Blob[]>,
  dispatch: AppDispatch,
  intervalms: React.MutableRefObject<number>,
  streamfilter: React.MutableRefObject<number>,
  setToothNumber: React.Dispatch<React.SetStateAction<number>>,
  setPosition: React.Dispatch<React.SetStateAction<string>>,
  setToothFeature: React.Dispatch<React.SetStateAction<ToothFeature>>,
  choiceCounter: React.MutableRefObject<number>,
  position: React.MutableRefObject<string>,
  toothNumber: React.MutableRefObject<number>,
  setClear: React.Dispatch<React.SetStateAction<boolean>>,
  prev_content: React.MutableRefObject<string>,
  session_id: React.MutableRefObject<string>,
  type: React.MutableRefObject<string>,
  currenttooth: React.MutableRefObject<Tooth>,
  wasVoiceDetectedRecently: React.MutableRefObject<boolean>,
  silenceStartTime: React.MutableRefObject<number | null>,
  setUndo: React.Dispatch<React.SetStateAction<boolean>>,
): Promise<void> => {
  /*
    Start recording for IMediaRecorder while streaming auio to voice assistant server
    Args:   
      recorder                (React.MutableRefObject<IMediaRecorder | null>): IMediaRecorder react reference object use to record wave audio segments
      stream                     (React.MutableRefObject<MediaStream | null>): MediaStream Object to stream audio from microphone 
      isRecording                           (React.MutableRefObject<boolean>):  Boolean to indicate if recorder is recording
      paused                                (React.MutableRefObject<boolean>):  Boolean to tell server to ignore transcripts
      audiofiles                             (React.MutableRefObject<Blob[]>):  Blob of audiofile in wav form
      dispatch:                                                 (AppDispatch):  Dispatch to invoke store action 
      intervalms:                            (React.MutableRefObject<number>):  (Depracated) Interval for SetInterval function
      streamfilter:                          (React.MutableRefObject<number>):  (Depracated) Volume/Amplitude filter
      setToothNumber:          (React.Dispatch<React.SetStateAction<number>>):  Dispatch function to change React state toothnumber
      setPosition:             (React.Dispatch<React.SetStateAction<string>>):  Dispatch function to change React state position
      setToothFeature:   (React.Dispatch<React.SetStateAction<ToothFeature>>):  Dispatch function to change React state toothfeature 
      choiceCounter:                         (React.MutableRefObject<number>):  (Depracated) used to count errors
      position:                              (React.MutableRefObject<string>):  A react ref object to reflect changes in position realtime
      toothNumber:                           (React.MutableRefObject<number>):  A react ref object to reflect changes in toothnumber realtime
      setClear:               (React.Dispatch<React.SetStateAction<boolean>>):  Dispatch function to change React state clear
      prev_content:                          (React.MutableRefObject<string>):  (Depracated) used to store previous data before new changes
      session_id:                            (React.MutableRefObject<string>):  Unique Identifier per user per session of voice assistant api
      type:                                  (React.MutableRefObject<string>):  String Identifier to invoke either (free/keyword) mode
      currenttooth:                           (React.MutableRefObject<Tooth>):  A ref object containing tooth number, position, type, pause
      wasVoiceDetectedRecently:             (React.MutableRefObject<boolean>):  Boolean to check for voce activity on streamed audio
      silenceStartTime:               (React.MutableRefObject<number | null>):  Inital time used to measure silence duration
      setUndo:                (React.Dispatch<React.SetStateAction<boolean>>):  Dispatch function used to change react state undo and to invoke undo command
  */
  const constraints = {
    audio: true,
  }

  const options = {
    mimeType: 'audio/wav',
  }
  try {
    await register(await connect())
  } catch (e) {
    const error = e
  }
  stream.current = await navigator.mediaDevices.getUserMedia(constraints)
  const audioContext = new AudioContext({ sampleRate: 48000 })
  const mediaStreamAudioSourceNode = new MediaStreamAudioSourceNode(
    audioContext,
    { mediaStream: stream.current },
  )
  const mediaStreamAudioDestinationNode = new MediaStreamAudioDestinationNode(
    audioContext,
  )

  mediaStreamAudioSourceNode.connect(mediaStreamAudioDestinationNode)
  recorder.current = new MediaRecorder(
    mediaStreamAudioDestinationNode.stream,
    options,
  )
  setuponDataAvailablem(
    recorder,
    paused,
    mode,
    audiofiles,
    dispatch,
    intervalms,
    streamfilter,
    setToothNumber,
    setPosition,
    setToothFeature,
    choiceCounter,
    position,
    toothNumber,
    setClear,
    prev_content,
    session_id,
    type,
    currenttooth,
    wasVoiceDetectedRecently,
    silenceStartTime,
    setUndo,
  )
  if (!recorder.current) {
    throw new Error('Recorder must not be none')
  }
  recorder.current.start(1000)

  isRecording.current = true
}
